import Button from "@src/components/Button/Button";
import Links from "@src/components/Forms/Links/Links";
import Loader from "@src/components/Loader/Loader";
import Modal from "@src/components/Modal/Modal";
import Page from "@src/layout/Page";
import {
    createLink,
    deleteLink,
    getAllLinks,
    updateLink,
} from "@src/services/requests/links";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import * as yup from "yup";
import toast from "react-hot-toast";
import { yupResolver } from "@hookform/resolvers/yup";
import { useSprings, animated, config } from "@react-spring/web";
import LinkCard from "@src/components/LinkCard/LinkCard";
import "./styles/links.sass";
import { Link, LinkDTO } from "@src/types/global";

const LinksIndex = () => {
    return (
        <Page>
            <h2>Links</h2>
            <Content />
        </Page>
    );
};
enum RelationshipType {
    FRIENDS = "friends",
    LOVERS = "lovers",
    ENEMIES = "enemies",
    ACQUAINTANCES = "acquaintances",
    FAMILY = "family",
}
const Content = () => {
    const emptyValues = {
        id: undefined,
        character_A_version_id: undefined,
        character_B_version_id: undefined,
        "character_A_version-name": undefined,
        relationship_type: RelationshipType.FRIENDS,
    } as any;

    const [values, setValues] = useState(emptyValues);
    const [type, setType] = useState<"create" | "update">("create");
    const { data, isPending, isSuccess, isError } = useQuery({
        queryKey: ["links"],
        queryFn: getAllLinks,
        notifyOnChangeProps: "all",
    });

    const [springs, _api] = useSprings(
        data != undefined ? data.length : 0,
        (i) => ({
            from: { opacity: 0 },
            to: { opacity: 1 },
            delay: i * 100,
            config: config.stiff,
        }),
        [isSuccess],
    );
    const [isOpen, setIsOpen] = useState(false);
    function handleShowForm(): void {
        setIsOpen(true);
    }

    const schema = yup.object().shape({
        id: yup.string().nullable(),
        relationship_type: yup
            .string()
            .required()
            .oneOf(Object.values(RelationshipType)),
        character_A_version_id: yup.string().required(),
        character_B_version_id: yup.string().required(),
    });
    const methods = useForm({
        resolver: yupResolver(schema),
        defaultValues: values,
        values,
    });
    const queryClient = useQueryClient();
    const mutation = useMutation({
        mutationKey: ["links"],
        mutationFn: type == "create" ? createLink : updateLink,
        onSuccess: (data) => {
            setIsOpen(false);
            toast.success("Link successfully created");
            methods.reset();
            if (type == "create") {
                queryClient.setQueryData(["links"], (oldData: Link[]) => {
                    oldData.push(data);
                    return oldData;
                });
            }
            if (type == "update") {
                queryClient.setQueryData(["links"], (oldData: Link[]) => {
                    const idToUpdate = oldData.findIndex(
                        (link) => link.id == data.id,
                    );
                    oldData[idToUpdate] = data;
                    return oldData;
                });
            }
        },
        onError: () => {
            toast.error("An error occured while saving the link.");
        },
    });
    const deleteMutation = useMutation({
        mutationKey: ["links"],
        mutationFn: deleteLink,
        onSuccess: (_data, id) => {
            setIsOpen(false);
            toast.success("Link successfully deleted");
            methods.reset();
            queryClient.setQueryData(["links"], (oldData: Link[]) => {
                const newData = oldData.filter((link) => link.id != id);
                return newData;
            });
        },
        onError: () => {
            toast.error("An error occured while deleting the link.");
        },
    });

    /** Submits the form */
    function submit(data: LinkDTO) {
        mutation.mutate(data);
    }

    /**
     * Transforms a link into default values in the shape expected by the form
     */
    function toForm(link: Link): LinkDTO {
        return {
            ...link,
            character_B_version_id: link.character_B_version.id,
            "character_B_version_id-name": link.character_B_version.name,
            character_A_version_id: link.character_A_version.id,
            "character_A_version_id-name": link.character_A_version.name,
            relationship_type: link.relationship_type,
        };
    }
    if (isPending) {
        return <Loader />;
    }

    if (isSuccess && (data == undefined || data.length == 0)) {
        return (
            <div>
                <p>You don't have any links.</p>
                <Button
                    type="button"
                    preset="primary"
                    fn={handleShowForm}
                    className="create-link"
                >
                    Create a link
                </Button>
                <Modal isOpen={isOpen} closeModalFn={() => setIsOpen(false)}>
                    <FormProvider {...methods}>
                        <Links submitFn={mutation.mutate} />

                        <Button
                            type="submit"
                            preset="primary"
                            fn={methods.handleSubmit(submit)}
                            isLoading={mutation.isPending}
                        >
                            Submit
                        </Button>
                    </FormProvider>
                </Modal>
            </div>
        );
    }
    if (isSuccess && data != undefined) {
        return (
            <div>
                <div className="links-list">
                    {springs.map((prop, i) => (
                        <animated.div key={data[i].id} style={prop}>
                            <LinkCard
                                link={data[i]}
                                editFn={() => {
                                    setValues(toForm(data[i]));
                                    setType("update");
                                    handleShowForm();
                                }}
                                deleteFn={() =>
                                    deleteMutation.mutate(data[i].id)
                                }
                            />
                        </animated.div>
                    ))}
                </div>

                <Button
                    className="create-link"
                    type="button"
                    preset="primary"
                    fn={handleShowForm}
                >
                    Create a link
                </Button>
                <Modal
                    isOpen={isOpen}
                    closeModalFn={() => {
                        setIsOpen(false);
                        setValues(emptyValues);
                    }}
                >
                    <FormProvider {...methods}>
                        <Links submitFn={mutation.mutate} />

                        <Button
                            type="submit"
                            preset="primary"
                            fn={methods.handleSubmit(submit)}
                            isLoading={mutation.isPending}
                        >
                            Submit
                        </Button>
                    </FormProvider>
                </Modal>
            </div>
        );
    }
    if (isError) {
        return (
            <div>
                Charsh couldn't fetch your content. Try again later. If the
                issue persists hmu
            </div>
        );
    }
};

export default LinksIndex;
